import { useCallback, useEffect, useState, type JSX } from 'react';
import { Play, Printer, Download, Save, Trash2, Loader2 } from 'lucide-react';
import type { QuotaPolicy } from '@cca/xero-shared';
import {
  api,
  type ExclusionCategory,
  type QuotaRunResponse,
  type SavedPolicy,
  type BaselineResult,
  type PublishedPolicy,
} from './api/client';
import { PolicyForm, printQuotaRun, downloadRunCsv } from '@cca/quota-ui';
import { defaultPolicy } from './pages/policyDefaults';
import { fmt } from '@cca/ui';

export function Explorer(): JSX.Element {
  const [categories, setCategories] = useState<ExclusionCategory[]>([]);
  const [baseline, setBaseline] = useState<BaselineResult | null>(null);
  const [published, setPublished] = useState<PublishedPolicy[]>([]);
  const [mine, setMine] = useState<SavedPolicy[]>([]);
  const [policy, setPolicy] = useState<QuotaPolicy>(() => defaultPolicy(2026));
  const [name, setName] = useState('My policy');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [run, setRun] = useState<QuotaRunResponse | null>(null);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState('');

  const reloadMine = useCallback(async () => {
    setMine((await api.listMyPolicies()).policies);
  }, []);

  useEffect(() => {
    void (async () => {
      const [cats, base, pub] = await Promise.all([
        api.categories().then((r) => r.categories),
        api.baseline().then((r) => r.baseline),
        api
          .published()
          .then((r) => r.policies)
          .catch(() => []),
      ]);
      setCategories(cats);
      setPublished(pub);
      if (base) {
        setBaseline(base);
        setPolicy(defaultPolicy(base.assessment_year));
      }
      await reloadMine();
    })();
  }, [reloadMine]);

  // The run's invoice year IS the policy's Invoice Year — one control, in the
  // Policy panel (the separate top-bar year input was a confusing duplicate).
  const doRun = async (): Promise<void> => {
    if (!policy.effective_assessment_year) {
      setMsg('Set the Invoice Year in the Policy panel first.');
      return;
    }
    setRunning(true);
    setMsg('');
    try {
      const res = await api.whatif(policy, policy.effective_assessment_year);
      // Suppress the 'missing a year' data flag (null_income) in the public
      // calculator: with a future invoice year every church trips it, and on
      // the printout it reads like an error. The Finance Portal keeps it.
      setRun({
        ...res,
        results: res.results.map((r) => ({
          ...r,
          flags: r.flags.filter((f) => f !== 'null_income'),
        })),
      });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const save = async (): Promise<void> => {
    if (!name.trim()) return;
    if (editingId) await api.updateMyPolicy(editingId, name.trim(), policy);
    else {
      const r = await api.saveMyPolicy(name.trim(), policy);
      setEditingId(r.id);
    }
    setMsg('Saved.');
    await reloadMine();
  };

  const loadMine = (p: SavedPolicy): void => {
    setPolicy(p.policy);
    setName(p.name);
    setEditingId(p.id);
    setRun(null);
  };
  const loadPublished = (p: PublishedPolicy): void => {
    setPolicy(p.policy);
    setName(`From “${p.name}”`);
    setEditingId(null);
    setRun(null);
  };
  const remove = async (id: string): Promise<void> => {
    await api.deleteMyPolicy(id);
    if (editingId === id) setEditingId(null);
    await reloadMine();
  };

  const baseTotal = baseline?.total_payable ?? null;
  const delta = run && baseTotal != null ? run.total_payable - baseTotal : null;
  // Per-church figures from the registrar's baseline run — computed server-side
  // from the admin-set policy, so the participant's levers can't move them.
  // These are the numbers that sum to the top-bar baseline total.
  const baseByChurch = new Map(
    (baseline?.results ?? []).map((r) => [r.church_id, r.computable ? r.payable_quota : null]),
  );

  // One Run button, rendered twice: hard left on the Save/New row, and again
  // at the foot of the Policy form so a long edit doesn't end with a scroll-up.
  const runButton = (
    <button style={btnSm} disabled={running} onClick={() => void doRun()}>
      {running ? <Loader2 size={14} className="spin" /> : <Play size={14} />} Run
    </button>
  );

  return (
    <div
      style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, alignItems: 'start' }}
    >
      {/* Left: my policies + published */}
      <aside style={panel}>
        <h2 style={h2}>My policies</h2>
        <input
          style={input}
          placeholder="Policy name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
          {runButton}
          <button style={btnSm} onClick={() => void save()}>
            <Save size={14} /> {editingId ? 'Update' : 'Save'}
          </button>
          <button
            style={btnSmGhost}
            onClick={() => {
              setEditingId(null);
              setName('My policy');
              setPolicy(defaultPolicy(baseline?.assessment_year ?? 2026));
              setRun(null);
            }}
          >
            New
          </button>
        </div>
        {mine.length === 0 && <p style={hint}>None saved yet.</p>}
        {mine.map((p) => (
          <div key={p.id} style={listRow}>
            <button style={listLink} onClick={() => loadMine(p)}>
              {p.name}
            </button>
            <button style={iconBtn} title="Delete" onClick={() => void remove(p.id)}>
              <Trash2 size={13} />
            </button>
          </div>
        ))}

        {published.length > 0 && (
          <>
            <h2 style={{ ...h2, marginTop: 20 }}>Registrar’s preferred</h2>
            {published.map((p) => (
              <div key={p.id} style={listRow}>
                <button style={listLink} onClick={() => loadPublished(p)}>
                  {p.name}
                </button>
              </div>
            ))}
          </>
        )}
      </aside>

      {/* Right: policy editor + run + results */}
      <section>
        {run && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
            <button
              style={btnSmGhost}
              onClick={() =>
                printQuotaRun({
                  run,
                  policy,
                  categories,
                  mode: 'draft',
                  attribution: 'the CCAANZ Quota Model',
                  ...(baseline
                    ? {
                        baseline: {
                          byChurch: baseByChurch,
                          total: baseline.total_payable,
                          label: baseline.label,
                        },
                      }
                    : {}),
                })
              }
            >
              <Printer size={14} /> Print
            </button>
            <button style={btnSmGhost} onClick={() => downloadRunCsv(run, policy, categories)}>
              <Download size={14} /> CSV
            </button>
          </div>
        )}

        {msg && <p style={{ color: '#005487', fontSize: 13, marginBottom: 8 }}>{msg}</p>}

        {run && (
          <div style={{ ...panel, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
              <Stat label="Total payable (your policy)" value={fmt(run.total_payable)} />
              {baseTotal != null && (
                <Stat
                  label={
                    baseline?.label?.trim() ? `Baseline — ${baseline.label}` : 'Baseline total'
                  }
                  value={fmt(baseTotal)}
                />
              )}
              {delta != null && (
                <Stat
                  label="Difference vs baseline"
                  value={(delta >= 0 ? '+' : '−') + fmt(Math.abs(delta))}
                  color={delta > 0 ? '#c62828' : '#2e7d32'}
                />
              )}
              <Stat label="Churches" value={String(run.count)} />
            </div>
          </div>
        )}

        {run && (
          <div style={panel}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr
                  style={{ textAlign: 'left', color: '#5c4f33', borderBottom: '2px solid #e5e0d5' }}
                >
                  <th style={th}>Church</th>
                  <th style={thR}>Baseline</th>
                  <th style={thR}>Payable (yours)</th>
                  <th style={thR}>Current</th>
                </tr>
              </thead>
              <tbody>
                {run.results.map((r) => {
                  const b = baseByChurch.get(r.church_id);
                  return (
                    <tr key={r.church_id} style={{ borderBottom: '1px solid #f0ebde' }}>
                      <td style={td}>{r.name}</td>
                      <td style={tdR}>{b == null ? '—' : fmt(b)}</td>
                      <td style={tdR}>{r.computable ? fmt(r.payable_quota) : '—'}</td>
                      <td style={tdR}>{fmt(r.current_quota)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ ...panel, marginTop: 16 }}>
          <h2 style={h2}>Policy</h2>
          <PolicyForm policy={policy} onChange={setPolicy} categories={categories} hideVersion />
          <div style={{ marginTop: 14 }}>{runButton}</div>
        </div>
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}): JSX.Element {
  return (
    <div>
      <div
        style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color ?? '#1a3a5c' }}>{value}</div>
    </div>
  );
}

const panel: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e0d5',
  borderRadius: 8,
  padding: 16,
};
const h2: React.CSSProperties = {
  fontSize: 14,
  color: '#005487',
  marginBottom: 10,
  fontWeight: 600,
};
const hint: React.CSSProperties = { fontSize: 12, color: '#6b7280' };
const input: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '8px 10px',
  marginBottom: 10,
  border: '1px solid #d8d2c4',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};
const btnSm: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '6px 12px',
  background: '#85754d',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
const btnSmGhost: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  // 1px less padding than btnSm all round — the border makes up the difference,
  // so ghost and solid buttons land on identical outer dimensions.
  padding: '5px 11px',
  background: '#fff',
  color: '#005487',
  border: '1px solid #c7d2e0',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
const listRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '3px 0',
};
const listLink: React.CSSProperties = {
  flex: 1,
  textAlign: 'left',
  background: 'none',
  border: 'none',
  color: '#1a3a5c',
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
  padding: '3px 0',
};
const iconBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#9ca3af',
  cursor: 'pointer',
  padding: 2,
};
const th: React.CSSProperties = { padding: '6px 8px', fontWeight: 600 };
const thR: React.CSSProperties = { ...th, textAlign: 'right' };
const td: React.CSSProperties = { padding: '6px 8px' };
const tdR: React.CSSProperties = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
